import React, { useContext, useEffect, useId, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { EventBasedTriggerMetadata } from '@reach/shared-types/business-automation';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import { useScrollIndicator } from '../useScrollIndicator';
import { useSegmentDefinitions } from '../useSegmentDefinitions';
import { ExcludeSegmentPanel } from '../segment-picker/ExcludeSegmentPanel';
import { SegmentOptionList } from '../segment-picker/SegmentOptionList';
import { SegmentPickerTabs, type SegmentPickerTab } from '../segment-picker/SegmentPickerTabs';
import { getSegmentPickerPanelId, getSegmentPickerTabId } from '../segment-picker/segment-picker-ids';
import { SegmentReuseWarning } from '../segment-picker/SegmentReuseWarning';
import {
  getSegmentDefaultIds,
  getTemplateSegmentUsage,
  omitSegmentUsages,
  type SegmentUsage,
} from '../segment-picker/segment-usage';
import { SEGMENT_USAGE_TAGS } from '../segment-picker/segment-picker-copy';
import { SegmentListStates } from './SegmentListStates';
import {
  getNextExclusions,
  SEGMENT_TRIGGER_AUDIENCE_DESCRIPTION,
  SEGMENT_TRIGGER_TAB_LABEL,
  TRIGGER_DIALOG_SUBTITLES,
  TRIGGER_SEGMENT_INTRO,
} from './trigger-exclusions';
import styles from './TriggerStep.module.css';

const TRIGGER_USAGE: SegmentUsage = { kind: 'trigger' };

interface SegmentTriggerConfigDialogProps {
  onClose: () => void;
  triggerMetadata: EventBasedTriggerMetadata;
}

export const SegmentTriggerConfigDialog: React.FC<SegmentTriggerConfigDialogProps> = ({
  onClose,
  triggerMetadata,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateTrigger, updateExclusions, state } = editor;
  const { excludeSegmentDefaults } = state.localContents;
  const { data: segmentDefinitions, isLoading, error } = useSegmentDefinitions();

  const currentSegmentId = triggerMetadata.eventFilter?.segment_id ?? null;
  const [activeTab, setActiveTab] = useState<SegmentPickerTab>('include');
  const pickerId = useId();
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(currentSegmentId);
  const [excludedIds, setExcludedIds] = useState<string[]>(() =>
    getSegmentDefaultIds(excludeSegmentDefaults),
  );
  const [customTriggerCopy, setCustomTriggerCopy] = useState(triggerMetadata.customTriggerCopy ?? '');
  const { mainRef, hasMoreBelow, onScroll, checkScroll } = useScrollIndicator();

  const usedElsewhere = omitSegmentUsages(
    getTemplateSegmentUsage(state.localContents),
    usage => usage.kind === 'trigger' || usage.kind === 'exclusion',
  );
  const segments = segmentDefinitions ?? [];
  const hasSegments = segments.length > 0;
  const selectedSegmentIds = selectedSegmentId ? [selectedSegmentId] : [];
  const selectedSegment = segments.find(segment => segment.canonicalVersionId === selectedSegmentId);

  useEffect(() => {
    setSelectedSegmentId(triggerMetadata.eventFilter?.segment_id ?? null);
    setCustomTriggerCopy(triggerMetadata.customTriggerCopy ?? '');
  }, [triggerMetadata]);

  useEffect(() => {
    checkScroll();
  }, [segmentDefinitions, activeTab, checkScroll]);

  const handleOK = () => {
    const newTriggerMetadata = structuredClone(triggerMetadata);
    newTriggerMetadata.eventFilter.segment_id = selectedSegmentId || '';
    newTriggerMetadata.customTriggerCopy = customTriggerCopy || undefined;
    updateTrigger(newTriggerMetadata);
    const nextExclusions = getNextExclusions(excludeSegmentDefaults, excludedIds);
    if (nextExclusions) {
      updateExclusions(nextExclusions);
    }
    onClose();
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.segmentTriggerDialog} open>
        <header>
          <hgroup>
            <h2>Select Audience Segment</h2>
            <p>{TRIGGER_DIALOG_SUBTITLES[activeTab]}</p>
          </hgroup>
          <button onClick={onClose} type="button" aria-label="Close">
            <X />
          </button>
        </header>

        <section className={styles.triggerCopySection}>
          <label>
            Trigger label
            <input
              type="text"
              value={customTriggerCopy}
              onChange={e => setCustomTriggerCopy(e.target.value)}
              placeholder="e.g. When a new lead signs up"
            />
          </label>
          <p>Optional — shown to customers in the automation view.</p>
        </section>

        <SegmentPickerTabs
          pickerId={pickerId}
          activeTab={activeTab}
          onChange={setActiveTab}
          includeLabel={SEGMENT_TRIGGER_TAB_LABEL}
          exclusionCount={excludedIds.length}
        />

        <div className={styles.mainWrapper}>
          <main
            ref={mainRef}
            onScroll={onScroll}
            id={getSegmentPickerPanelId(pickerId)}
            role="tabpanel"
            aria-labelledby={getSegmentPickerTabId(pickerId, activeTab)}>
            <SegmentListStates isLoading={isLoading} error={error} isEmpty={!hasSegments} />
            {hasSegments && activeTab === 'include' && (
              <>
                <p className={styles.triggerIntro}>{TRIGGER_SEGMENT_INTRO}</p>
                {selectedSegmentId && (
                  <SegmentReuseWarning
                    segmentName={selectedSegment?.name}
                    pickedUsage={TRIGGER_USAGE}
                    usagesElsewhere={usedElsewhere.get(selectedSegmentId)}
                  />
                )}
                <SegmentOptionList
                  segments={segments}
                  selectedIds={selectedSegmentIds}
                  onSelect={setSelectedSegmentId}
                  usedElsewhere={usedElsewhere}
                  blocked={{ segmentIds: excludedIds, tag: SEGMENT_USAGE_TAGS.exclusion }}
                />
              </>
            )}
            {hasSegments && activeTab === 'exclude' && (
              <ExcludeSegmentPanel
                segments={segments}
                excludedIds={excludedIds}
                onChange={setExcludedIds}
                includedId={selectedSegmentId}
                includedTag={SEGMENT_USAGE_TAGS.trigger}
                audienceDescription={SEGMENT_TRIGGER_AUDIENCE_DESCRIPTION}
                usedElsewhere={usedElsewhere}
              />
            )}
          </main>
          {hasMoreBelow && (
            <div className={styles.scrollIndicator}>
              <span className={styles.scrollIndicatorPill}>
                <ChevronDown size={12} />
                More below
              </span>
            </div>
          )}
        </div>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>
    </>
  );
};
